import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Settings, Plus, Pencil } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { code: '', description: '', auto_create_on_gr: false, status: 'Active' };

const InspectionTypes = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('inspection_type');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quality/inspection-types`, { withCredentials: true });
      setRows(res.data);
    } catch (e) { toast.error('Failed to load inspection types'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditing(false); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEditing(true); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/quality/inspection-types/${form.code}`, form, { withCredentials: true });
      else await axios.post(`${API}/quality/inspection-types`, form, { withCredentials: true });
      toast.success('Saved'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="inspection-types-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100 text-teal-700"><Settings className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inspection Types</h1>
              <p className="text-gray-600 text-sm mt-0.5">Kinds of inspection and auto-creation rules.</p>
            </div>
          </div>
          {writable && <Button onClick={openNew} data-testid="new-inspection-type-btn"><Plus className="h-4 w-4 mr-2" />New Type</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Description</TableHead>
              <TableHead>Auto-create on GR</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">No inspection types yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.code} data-testid={`inspection-type-row-${r.code}`}>
                    <TableCell className="font-medium">{r.code}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.auto_create_on_gr ? <Badge>Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                    <TableCell><Badge variant={r.status === 'Active' ? 'default' : 'secondary'}>{r.status}</Badge></TableCell>
                    <TableCell>{writable && <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-inspection-type-${r.code}`}><Pencil className="h-4 w-4" /></Button>}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} Inspection Type</DialogTitle>
            <DialogDescription>Define an inspection type used when creating inspection lots.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Code</Label><Input data-testid="it-code" value={form.code} disabled={editing} maxLength={10} onChange={(e) => set('code', e.target.value)} placeholder="01" /></div>
            <div><Label>Description</Label><Input data-testid="it-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label htmlFor="auto_gr" className="cursor-pointer">Auto-create lot on Goods Receipt</Label>
              <Switch id="auto_gr" data-testid="it-auto-gr" checked={form.auto_create_on_gr} onCheckedChange={(v) => set('auto_create_on_gr', v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.code || !form.description} data-testid="save-inspection-type-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default InspectionTypes;
